import { Card as AntCard } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize, IconVariant } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';

export type StatCardSize = 'xSmall' | 'small' | 'medium' | 'large';

export interface StatCardProps {
  label: string;
  value: string | number;
  size?: StatCardSize;
  bgColor?: boolean;
  outline?: boolean;
  showHelper?: boolean;
  helperText?: string;
  secondHelper?: boolean;
  secondHelperText?: string;
  /** Optional leading icon beside the label. Omitted by default. */
  labelIcon?: IconType;
  labelIconVariant?: IconVariant;
  labelIconColor?: string;
  /** Show a decorative info icon beside the label. */
  showLabelInfo?: boolean;
  /** Show a decorative info icon beside the value. */
  showValueInfo?: boolean;
  /** Leading icon for each helper row. Defaults to Mail; pass `null` to hide. */
  helperIcon?: IconType | null;
  /** Show a decorative info icon beside each helper row. */
  showHelperInfo?: boolean;
  className?: string;
  'data-testid'?: string;
}

const headlineVariantBySize: Record<
  StatCardSize,
  'heading3Bold' | 'heading4Bold'
> = {
  xSmall: 'heading4Bold',
  small: 'heading3Bold',
  medium: 'heading3Bold',
  large: 'heading3Bold'
};

const labelVariantBySize: Record<StatCardSize, 'body1Semi' | 'body2Bold'> = {
  xSmall: 'body2Bold',
  small: 'body2Bold',
  medium: 'body2Bold',
  large: 'body1Semi'
};

const gapBySize: Record<StatCardSize, string> = {
  xSmall: theme.dime.spacing.m,
  small: theme.dime.spacing.m,
  medium: theme.dime.spacing.l,
  large: theme.dime.spacing.l
};

const StyledCard = styled(AntCard)<{
  $size: StatCardSize;
  $bgColor: boolean;
  $outline: boolean;
}>`
  &.ant-card {
    display: inline-block;
    width: fit-content;
    border-radius: ${theme.dime.radius.m};
    background-color: ${({ $bgColor }) =>
      $bgColor
        ? theme.dime.colors.surface.secondary
        : theme.dime.colors.surface.primary};
    border: ${({ $outline }) =>
      $outline ? `1px solid ${theme.dime.colors.border.static}` : 'none'};
    box-shadow: none;

    .ant-card-body {
      display: flex;
      flex-direction: column;
      padding: ${({ $bgColor, $outline }) =>
        $bgColor || $outline ? theme.dime.spacing.l : 0};
      gap: ${({ $size }) => gapBySize[$size]};

      /* antd's float clearfix pseudo-elements become flex items once
         display: flex is set above, so they pick up the gap and add
         phantom space before/after the real content. */
      &::before,
      &::after {
        display: none;
      }
    }
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

const HelperGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.l};
`;

const HelperRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
`;

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  size = 'xSmall',
  bgColor = false,
  outline = false,
  showHelper = true,
  helperText,
  secondHelper = true,
  secondHelperText,
  labelIcon,
  labelIconVariant = IconVariant.Solid,
  labelIconColor = theme.dime.colors.icon.critical,
  showLabelInfo = false,
  showValueInfo = false,
  helperIcon,
  showHelperInfo = false,
  className,
  'data-testid': dataTestId
}) => {
  const hasHelperContent =
    showHelper &&
    (!!helperText || (secondHelper && !!secondHelperText));
  const resolvedHelperIcon =
    helperIcon === undefined ? IconType.Mail : helperIcon;

  const renderHelperRow = (text?: string) => {
    if (!text) return null;

    return (
      <HelperRow>
        {resolvedHelperIcon ? (
          <IconWrap
            type={resolvedHelperIcon}
            size={IconSize.Small}
            variant={IconVariant.Outline}
            style={{ color: theme.dime.colors.icon.link }}
          />
        ) : null}
        <Typography variant="body1" color={theme.dime.colors.text.secondary}>
          {text}
        </Typography>
        {showHelperInfo ? (
          <IconWrap
            type={IconType.Information}
            size={IconSize.Small}
            variant={IconVariant.Outline}
            style={{ color: theme.dime.colors.icon.secondary }}
          />
        ) : null}
      </HelperRow>
    );
  };

  return (
    <StyledCard
      $size={size}
      $bgColor={bgColor}
      $outline={outline}
      bordered={false}
      className={className}
      data-testid={dataTestId ?? 'stat-card'}
    >
      <LabelRow>
        {labelIcon ? (
          <IconWrap
            type={labelIcon}
            size={IconSize.Small}
            variant={labelIconVariant}
            style={{ color: labelIconColor }}
          />
        ) : null}
        <Typography
          variant={labelVariantBySize[size]}
          color={theme.dime.colors.text.secondary}
        >
          {label}
        </Typography>
        {showLabelInfo ? (
          <IconWrap
            type={IconType.Information}
            size={IconSize.Small}
            variant={IconVariant.Outline}
            style={{ color: theme.dime.colors.icon.secondary }}
          />
        ) : null}
      </LabelRow>
      <ValueRow>
        <Typography
          variant={headlineVariantBySize[size]}
          color={theme.dime.colors.text.primary}
        >
          {value}
        </Typography>
        {showValueInfo ? (
          <IconWrap
            type={IconType.Information}
            size={IconSize.Small}
            variant={IconVariant.Outline}
            style={{ color: theme.dime.colors.icon.secondary }}
          />
        ) : null}
      </ValueRow>
      {hasHelperContent ? (
        <HelperGroup>
          {renderHelperRow(helperText)}
          {secondHelper && secondHelperText
            ? renderHelperRow(secondHelperText)
            : null}
        </HelperGroup>
      ) : null}
    </StyledCard>
  );
};
